/**
 * Data Hub categorical-metric definitions (SE-4168)
 *
 * Single source of truth tying each Data Hub metric's display label to its
 * backend GraphQL field names. The spider graph, dot plot, distribution plots,
 * individual metric insights, comparison configs, and metric-description modal
 * all derive from these definitions so a label and its underlying field can
 * never drift apart.
 *
 * Each metric exposes a `globalField` (career / time-decayed score, read from
 * `bioData`) and a `positionalField` (per-season score, read from
 * `seasonCategoricalScores`). Goalkeeper metrics use a single `Trans` field for
 * both. `Physical Score` and `GPR` are global-only and are handled separately
 * by the consumers, so they are intentionally absent here.
 */

/** A single Data Hub categorical metric and its backend field bindings */
export interface CategoricalMetricDefinition {
  /** Display label used in dropdowns and the metric-description modal */
  readonly label: string;
  /**
   * Stable key matching the `SeasonMetricAveragesResult` field and the
   * individual-metric selector value (e.g. "passing", "gkCrossIntercept").
   */
  readonly key: string;
  /** `bioData` field for the career / global (time-decayed) score */
  readonly globalField: string;
  /** `seasonCategoricalScores` field for the per-season / positional score */
  readonly positionalField: string;
  /**
   * Per-season field used by Individual Metric Insights for the player's own
   * line. Defaults to `positionalField`; `Shooting` overrides it to the global
   * field to preserve existing behaviour.
   */
  readonly individualField?: string;
}

/**
 * Outfield categorical metrics — the original four plus the fourteen added in
 * SE-4168, kept in that order so existing default selections are unaffected.
 */
export const OUTFIELD_CATEGORICAL_METRICS = [
  {
    label: "Carrying",
    key: "carrying",
    globalField: "carryingTransGlobalCategoricalScore",
    positionalField: "carryingTransPositionalCategoricalScore",
  },
  {
    label: "Dribbling",
    key: "dribbling",
    globalField: "dribblingTransGlobalCategoricalScore",
    positionalField: "dribblingTransPositionalCategoricalScore",
  },
  {
    label: "Passing",
    key: "passing",
    globalField: "passingTransGlobalCategoricalScore",
    positionalField: "passingTransPositionalCategoricalScore",
  },
  {
    label: "Shooting",
    key: "shooting",
    globalField: "shotLocationTransGlobalCategoricalScore",
    positionalField: "shotLocationTransPositionalCategoricalScore",
    // Preserves the pre-SE-4168 behaviour of plotting the global shot score.
    individualField: "shotLocationTransGlobalCategoricalScore",
  },
  {
    label: "Aerials",
    key: "aerials",
    globalField: "aerialsTransGlobalCategoricalScore",
    positionalField: "aerialsTransPositionalCategoricalScore",
  },
  {
    label: "Anticipating",
    key: "anticipating",
    globalField: "anticipatingTransGlobalCategoricalScore",
    positionalField: "anticipatingTransPositionalCategoricalScore",
  },
  {
    label: "Assisting",
    key: "assisting",
    globalField: "assistingTransGlobalCategoricalScore",
    positionalField: "assistingTransPositionalCategoricalScore",
  },
  {
    label: "Build Up",
    key: "buildup",
    globalField: "buildupTransGlobalCategoricalScore",
    positionalField: "buildupTransPositionalCategoricalScore",
  },
  {
    label: "Creating",
    key: "creating",
    globalField: "creatingTransGlobalCategoricalScore",
    positionalField: "creatingTransPositionalCategoricalScore",
  },
  {
    label: "Crossing",
    key: "crossing",
    globalField: "crossingTransGlobalCategoricalScore",
    positionalField: "crossingTransPositionalCategoricalScore",
  },
  {
    label: "Defensive Aerials",
    key: "defensiveAerials",
    globalField: "defensiveAerialsTransGlobalCategoricalScore",
    positionalField: "defensiveAerialsTransPositionalCategoricalScore",
  },
  {
    label: "Finishing",
    key: "finishing",
    globalField: "finishingTransGlobalCategoricalScore",
    positionalField: "finishingTransPositionalCategoricalScore",
  },
  {
    label: "Heading",
    key: "heading",
    globalField: "headingTransGlobalCategoricalScore",
    positionalField: "headingTransPositionalCategoricalScore",
  },
  {
    label: "Pressing",
    key: "pressing",
    globalField: "pressingTransGlobalCategoricalScore",
    positionalField: "pressingTransPositionalCategoricalScore",
  },
  {
    label: "Proactive Defending",
    key: "proactiveDefending",
    globalField: "proactiveDefendingTransGlobalCategoricalScore",
    positionalField: "proactiveDefendingTransPositionalCategoricalScore",
  },
  {
    label: "Set Piece Delivery",
    key: "setPieceDelivering",
    globalField: "setPieceDeliveringTransGlobalCategoricalScore",
    positionalField: "setPieceDeliveringTransPositionalCategoricalScore",
  },
  {
    label: "Shot Placement",
    key: "shotPlacement",
    globalField: "shotPlacementTransGlobalCategoricalScore",
    positionalField: "shotPlacementTransPositionalCategoricalScore",
  },
  {
    label: "Tackling",
    key: "tackling",
    globalField: "tacklingTransGlobalCategoricalScore",
    positionalField: "tacklingTransPositionalCategoricalScore",
  },
] as const satisfies readonly CategoricalMetricDefinition[];

/**
 * Goalkeeper categorical metrics — the original three plus the two added in
 * SE-4168. GK metrics share a single `Trans` field for global and positional.
 */
export const GK_CATEGORICAL_METRICS = [
  {
    label: "GK Passing",
    key: "gkPassing",
    globalField: "gkPassingTransCategoricalScore",
    positionalField: "gkPassingTransCategoricalScore",
  },
  {
    label: "Shot Stopping",
    key: "gkShotStopping",
    globalField: "gkShotStoppingTransCategoricalScore",
    positionalField: "gkShotStoppingTransCategoricalScore",
  },
  {
    label: "Positioning",
    key: "gkPositioning",
    globalField: "gkPositioningTransCategoricalScore",
    positionalField: "gkPositioningTransCategoricalScore",
  },
  {
    label: "Cross Interception",
    key: "gkCrossIntercept",
    globalField: "gkCrossInterceptTransCategoricalScore",
    positionalField: "gkCrossInterceptTransCategoricalScore",
  },
  {
    label: "Sweeper",
    key: "gkKeeperSweeper",
    globalField: "gkKeeperSweeperTransCategoricalScore",
    positionalField: "gkKeeperSweeperTransCategoricalScore",
  },
] as const satisfies readonly CategoricalMetricDefinition[];

/** Every categorical metric (outfield + GK), used by comparison extraction */
export const ALL_CATEGORICAL_METRICS: readonly CategoricalMetricDefinition[] = [
  ...OUTFIELD_CATEGORICAL_METRICS,
  ...GK_CATEGORICAL_METRICS,
];

/** Literal union of outfield metric keys (e.g. "passing" | "aerials" | ...) */
export type OutfieldMetricKey =
  (typeof OUTFIELD_CATEGORICAL_METRICS)[number]["key"];

/** Literal union of goalkeeper metric keys (e.g. "gkPassing" | "gkKeeperSweeper") */
export type GkMetricKey = (typeof GK_CATEGORICAL_METRICS)[number]["key"];

/**
 * SE-4456 Metric Description copy for the attributes added in SE-4168, keyed by
 * metric key. Each value is the metric-specific clause that completes the shared
 * "league-adjusted blend … last 3 years for …" sentence (see
 * `leagueAdjustedBlend` in constants). Kept here as data alongside the metric
 * definitions so the description list can be generated rather than hand-written.
 */
export const METRIC_DESCRIPTION_CLAUSES: Readonly<Record<string, string>> = {
  aerials:
    "how often this player contests aerial duels and how successful they are at winning them.",
  anticipating:
    "how often this player attempts interceptions and ball recoveries and how successful they are at completing them.",
  assisting:
    "how often this player plays passes that lead to shots and the quality of the chances those passes create.",
  buildup:
    "how often this player plays progressive passes in the first and second thirds of the pitch and how much value those passes add.",
  creating:
    "how often this player plays final-third and box passes and how much attacking value those passes generate.",
  crossing:
    "how often this player attempts crosses and how effective those crosses are at creating value.",
  defensiveAerials:
    "how often this player contests aerial duels in their own half and how successful they are at winning them.",
  finishing:
    "how often this player gets shots off and how well they finish relative to the quality of those chances.",
  heading:
    "how often this player attempts headed shots and the quality of the chances they generate from them.",
  pressing:
    "how often this player applies pressure to opponents and how successful those pressures are.",
  proactiveDefending:
    "how often this player attempts defensive actions and how consistently successful those actions are.",
  setPieceDelivering:
    "how often this player takes set pieces and how effective their deliveries are at creating value.",
  shotPlacement:
    "how often this player takes open-play shots with their feet and how well they place them relative to expectation.",
  tackling:
    "how often this player engages in duels, 50/50s, and fouls and how successful they are at coming out on top.",
  gkCrossIntercept:
    "how often this goalkeeper faces crosses into their box and how successful they are at intercepting them.",
  gkKeeperSweeper:
    "how often this goalkeeper makes sweeping actions outside their 18-yard box and how successful they are at them.",
} satisfies Partial<Record<OutfieldMetricKey | GkMetricKey, string>>;
